import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import Footer from '../organisms/Footer/Footer';
import ContactUs from '../molecules/ContactUs/ContactUs';
import AppsFaq from '../molecules/AppsFaq/AppsFaq';
import ContactHelp from '../molecules/ContactHelp/ContactHelp';
import { getAllGalleryImages, GalleryImage } from '../../app/helper/galleryService';

// Eye Icon Component
const EyeIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="w-10 h-10"
    >
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
        <circle cx="12" cy="12" r="3"></circle>
    </svg>
);

// Close Icon Component
const CloseIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="w-6 h-6"
    >
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
);

// Left Arrow Icon Component
const LeftArrowIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="w-8 h-8"
    >
        <polyline points="15 18 9 12 15 6"></polyline>
    </svg>
);

// Right Arrow Icon Component
const RightArrowIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="w-8 h-8"
    >
        <polyline points="9 18 15 12 9 6"></polyline>
    </svg>
);


const GalleryPage = () => {
    const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

    useEffect(() => {
        const fetchGalleryImages = async () => {
            try {
                const images = await getAllGalleryImages();
                setGalleryImages(images);
            } catch (error) {
                console.error('Error fetching gallery images:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchGalleryImages();
    }, []);

    const handleNextImage = () => {
        if (selectedImageIndex !== null) {
            setSelectedImageIndex((prev) =>
                prev !== null && prev < galleryImages.length - 1 ? prev + 1 : 0
            );
        }
    };

    const handlePrevImage = () => {
        if (selectedImageIndex !== null) {
            setSelectedImageIndex((prev) =>
                prev !== null && prev > 0 ? prev - 1 : galleryImages.length - 1
            );
        }
    };

    const handleThumbnailClick = (index: number) => {
        setSelectedImageIndex(index);
    };

    // Keyboard navigation for preview modal
    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (selectedImageIndex !== null) {
                if (e.key === 'ArrowRight') {
                    e.preventDefault();
                    handleNextImage();
                } else if (e.key === 'ArrowLeft') {
                    e.preventDefault();
                    handlePrevImage();
                } else if (e.key === 'Escape') {
                    setSelectedImageIndex(null);
                }
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [selectedImageIndex, galleryImages.length]);

    const fadeInUp = {
        hidden: { opacity: 0, y: 60 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.8, ease: "easeOut" }
        }
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.3
            }
        }
    };

    return (
        <div>
            <div className='overflow-x-hidden'>
                <div className='bg-gallery-bg bg-center bg-cover bg-no-repeat'>
                    <div className='bg-[#000000] bg-opacity-10'>
                        <motion.div
                            className='flex flex-col pb-4 sm:pb-6 md:pb-8 gap-4 sm:gap-6 justify-center items-center pt-32 sm:pt-40 md:pt-52'
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: false, amount: 0.3 }}
                            variants={staggerContainer}
                        >
                            <motion.div
                                className='text-white flex flex-col gap-2 relative text-center justify-center items-center p-4 sm:p-6 md:p-[10%] pb-[8%] sm:pb-[12%] md:pb-[16%]'
                                variants={fadeInUp}
                            >
                                <motion.div
                                    className='font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-2 uppercase !line-height-[1.4]'
                                    variants={fadeInUp}
                                >
                                    The Little Moments <br />
                                    That Make It All Worth It
                                </motion.div>
                                <motion.div
                                    className='font-normal text-base sm:text-lg md:text-xl lg:text-2xl leading-snug px-4 sm:px-8 md:px-0 !line-height-[1.8]'
                                    variants={fadeInUp}
                                >
                                    Our journey is shaped not just by projects and achievements, but by the <br />
                                    people behind them. This space highlights the joy, connection, and <br />
                                    togetherness that make The Sattva Tech a great place to work. <br />
                                </motion.div>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
                <div className='bg-darkGreyPrimary'>
                    <div>
                        <div className='text-white text-4xl font-bold text-center uppercase pt-10'>Life at <span className='text-lightBlue'> The Sattva Tech</span></div>
                        <div>
                            {loading ? (
                                <div className='flex justify-center items-center py-12'>
                                    <div className='text-white text-xl'>Loading gallery images...</div>
                                </div>
                            ) : galleryImages.length > 0 ? (
                                <div className='flex flex-wrap gap-8 px-16 py-12 justify-center items-center'>
                                    {galleryImages.map((image, index) => (
                                        <motion.div
                                            key={image.id}
                                            className='relative rounded-2xl overflow-hidden max-w-sm max-h-96 cursor-pointer group'
                                            initial="hidden"
                                            whileInView="visible"
                                            viewport={{ once: true }}
                                            variants={fadeInUp}
                                            onClick={() => setSelectedImageIndex(index)}
                                            transition={{ duration: 0.3, ease: "easeOut" }}
                                        >
                                            <img
                                                src={image.imageUrl}
                                                alt={image.title || 'Gallery image'}
                                                className='w-full h-full object-cover transition-transform duration-300 group-hover:scale-105'
                                                draggable={false}
                                            />

                                            {/* Dark overlay with title and eye icon */}
                                            <div className='absolute inset-0 bg-black bg-opacity-60 flex flex-col items-center justify-center gap-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300'>
                                                <h3 className='text-white text-xl font-semibold uppercase transform translate-y-2 group-hover:translate-y-0 group-hover:opacity-100 opacity-0 transition-all duration-300'>
                                                    {image.title || 'Gallery Image'}
                                                </h3>
                                                <div className='bg-lightBlue rounded-full p-3 flex items-center justify-center transform scale-75 opacity-0 group-hover:scale-100 group-hover:opacity-100 transition-all duration-300'>
                                                    <EyeIcon />
                                                </div>
                                            </div>

                                            {/* Blue border animation on hover - positioned on top */}
                                            <div className='absolute inset-0 border-2 border-lightBlue rounded-2xl pointer-events-none z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300' />
                                        </motion.div>
                                    ))}
                                </div>
                            ) : (
                                <div className='flex justify-center items-center py-12'>
                                    <div className='text-white text-xl'>No gallery images available yet.</div>
                                </div>
                            )}
                        </div>
                    </div>
                    <AppsFaq />
                    <ContactUs />
                    <ContactHelp title="Need help locating us?" buttonTitle="CONTACT US" />
                    <Footer footerContactInfo={true} />
                </div>
            </div>

            {/* Image Preview Modal */}
            {selectedImageIndex !== null && galleryImages[selectedImageIndex] && (
                <motion.div
                    className='fixed inset-0 bg-darkGreyPrimary flex flex-col items-center justify-center z-50 p-4'
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setSelectedImageIndex(null)}
                    onWheel={(e) => e.preventDefault()}
                    style={{ touchAction: 'none' }}
                >
                    {/* Close button */}
                    <button
                        onClick={() => setSelectedImageIndex(null)}
                        className='absolute top-4 right-4 z-20 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-2 transition-all text-white'
                    >
                        <CloseIcon />
                    </button>

                    {/* Thumbnails Row - Top */}
                    {galleryImages.length > 1 && (
                        <div className='flex gap-2 mb-4 overflow-x-auto max-w-[90vw] pb-2 scrollbar-hide'>
                            {galleryImages.map((image, index) => (
                                <motion.img
                                    key={image.id}
                                    src={image.imageUrl}
                                    alt={image.title || 'Gallery thumbnail'}
                                    className={`w-14 h-14 object-cover rounded cursor-pointer border-2 transition-all flex-shrink-0 ${selectedImageIndex === index
                                        ? 'border-lightBlue opacity-100 scale-110'
                                        : 'border-transparent opacity-50 hover:opacity-75'
                                        }`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleThumbnailClick(index);
                                    }}
                                    whileHover={{ scale: selectedImageIndex === index ? 1.1 : 1.15 }}
                                    draggable={false}
                                />
                            ))}
                        </div>
                    )}

                    {/* Main Preview Container */}
                    <div className='flex items-center justify-center gap-4 relative w-full max-w-6xl'>
                        {/* Left Arrow */}
                        {galleryImages.length > 1 && (
                            <motion.button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handlePrevImage();
                                }}
                                className='bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-3 text-white transition-all z-10'
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                            >
                                <LeftArrowIcon />
                            </motion.button>
                        )}

                        {/* Main Preview Image */}
                        <motion.div
                            className='relative flex-1 max-w-4xl'
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            onClick={(e) => e.stopPropagation()}
                            onTouchStart={(e) => e.preventDefault()}
                            onTouchMove={(e) => e.preventDefault()}
                            style={{
                                userSelect: 'none',
                                WebkitUserSelect: 'none',
                                touchAction: 'none',
                                WebkitTouchCallout: 'none'
                            }}
                        >
                            <img
                                src={galleryImages[selectedImageIndex].imageUrl}
                                alt={galleryImages[selectedImageIndex].title || 'Gallery image'}
                                className='w-full h-auto max-h-[70vh] object-contain rounded-lg gallery-preview-image'
                                draggable={false}
                                onContextMenu={(e) => e.preventDefault()}
                                onDragStart={(e) => e.preventDefault()}
                                onWheel={(e) => e.preventDefault()}
                                onTouchStart={(e) => e.preventDefault()}
                                onTouchMove={(e) => e.preventDefault()}
                                onTouchEnd={(e) => e.preventDefault()}
                                style={{
                                    pointerEvents: 'none',
                                    userSelect: 'none',
                                    WebkitUserSelect: 'none',
                                    WebkitTouchCallout: 'none',
                                    touchAction: 'none'
                                }}
                            />
                            {/* Image Title */}
                            {galleryImages[selectedImageIndex].title && (
                                <motion.div
                                    className='text-white text-center mt-4 text-xl font-semibold uppercase'
                                    initial={{ y: 10, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: 0.2 }}
                                >
                                    {galleryImages[selectedImageIndex].title}
                                </motion.div>
                            )}
                        </motion.div>

                        {/* Right Arrow */}
                        {galleryImages.length > 1 && (
                            <motion.button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleNextImage();
                                }}
                                className='bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full p-3 text-white transition-all z-10'
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                            >
                                <RightArrowIcon />
                            </motion.button>
                        )}
                    </div>
                </motion.div>
            )}
        </div>
    )
}

export default GalleryPage